/**
 * Posts editor — a job's positions to be covered, each bound to a billing item.
 *
 * The rate is NOT here. A post names the billing item; the rate comes from the
 * job's effective-dated rate line for that item. OT and holiday are separate
 * billing items with their own rates, never a multiplier.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { jobPostsAPI, billingItemsAPI, hoursTypesAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Wand2, Pencil, Archive, ArchiveRestore, X } from 'lucide-react';

const emptyPost = {
  post_name: '', post_description: '', billing_item_id: '',
  ot_billing_item_id: '', holiday_billing_item_id: '', hours_type_id: '',
  required_qualifications: [], is_active: true,
};

const ItemSelect = ({ value, onChange, items, testId, placeholder }) => (
  <Select value={value || '__none__'} onValueChange={(v) => onChange(v === '__none__' ? '' : v)}>
    <SelectTrigger data-testid={testId}><SelectValue placeholder={placeholder || 'Select…'} /></SelectTrigger>
    <SelectContent className="max-h-72">
      <SelectItem value="__none__">— None —</SelectItem>
      {items.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
    </SelectContent>
  </Select>
);

export default function PostsEditor({ jobId, canEdit, meta }) {
  const [posts, setPosts] = useState([]);
  const [items, setItems] = useState([]);
  const [hoursTypes, setHoursTypes] = useState([]);
  const [proposals, setProposals] = useState(null);
  const [draft, setDraft] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [p, bi, ht] = await Promise.all([
        jobPostsAPI.list(jobId, { include_inactive: true }),
        billingItemsAPI.list(),
        hoursTypesAPI.list(false).catch(() => ({ data: [] })),
      ]);
      setPosts(p.data);
      setItems((bi.data || []).filter(i => i.is_active));
      setHoursTypes(ht.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load posts');
    }
  };
  useEffect(() => { if (jobId) load(); }, [jobId]);  // eslint-disable-line react-hooks/exhaustive-deps

  const descriptions = useMemo(
    () => (meta?.lookups?.post_description || []).map(x => x.value), [meta]);
  const itemName = (id) => items.find(i => i.id === id)?.name || '—';

  const save = async () => {
    if (!draft.post_name.trim()) { toast.error('The post needs a name'); return; }
    if (!draft.billing_item_id) { toast.error('Pick the billing item for regular hours'); return; }
    setBusy(true);
    try {
      if (editingId) await jobPostsAPI.update(editingId, draft);
      else await jobPostsAPI.create(jobId, draft);
      toast.success('Post saved');
      setDraft(null); setEditingId(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const runProposals = async () => {
    try {
      const { data } = await jobPostsAPI.proposals(jobId);
      if (!data.proposals.length) { toast.info('Nothing left to propose for this job'); return; }
      setProposals(data.proposals);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not build proposals'); }
  };

  const applyProposals = async () => {
    const rows = proposals.filter(p => p.billing_item_id);
    if (!rows.length) { toast.error('Each post needs a billing item before it can be created'); return; }
    setBusy(true);
    try {
      const { data } = await jobPostsAPI.seed(jobId, rows.map(r => ({
        post_name: r.post_name, post_description: r.post_description,
        billing_item_id: r.billing_item_id,
        ot_billing_item_id: r.ot_billing_item_id || null,
        holiday_billing_item_id: r.holiday_billing_item_id || null,
      })));
      toast.success(`${data.created} post(s) created`);
      setProposals(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not create posts'); }
    finally { setBusy(false); }
  };

  const toggle = async (post) => {
    try { await jobPostsAPI.setActive(post.id, !post.is_active); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not update'); }
  };

  return (
    <div className="space-y-4" data-testid="posts-editor">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-fg-muted max-w-2xl">
          A post is a position to be covered. It names the billing item that prints on the
          invoice; the rate comes from this job's rate card, effective on the day worked.
        </p>
        {canEdit && (
          <div className="flex gap-2 shrink-0">
            <Button size="sm" variant="outline" onClick={runProposals} data-testid="posts-propose">
              <Wand2 className="w-4 h-4 mr-2" />Suggest from this job
            </Button>
            <Button size="sm" onClick={() => { setDraft({ ...emptyPost }); setEditingId(null); }}
              data-testid="posts-add">
              <Plus className="w-4 h-4 mr-2" />Add post
            </Button>
          </div>
        )}
      </div>

      {proposals && (
        <Card><CardContent className="p-4 space-y-3" data-testid="posts-proposals">
          <p className="text-sm font-medium">Suggested posts</p>
          <p className="text-xs text-fg-muted">
            Taken from the job's officer classifications, then its labor rate lines. Anything
            the catalog could not match is left blank on purpose — pick the billing item
            rather than let it be guessed.
          </p>
          {proposals.map((p, idx) => (
            <div key={idx} className="flex flex-wrap items-end gap-3 border-b border-line-subtle pb-3">
              <div className="min-w-[180px]">
                <Label className="text-xs">Post</Label>
                <Input className="h-8" value={p.post_name}
                  onChange={(e) => setProposals(ps => ps.map((x, i) => i === idx ? { ...x, post_name: e.target.value } : x))} />
              </div>
              <div className="min-w-[240px]">
                <Label className="text-xs">Billing item (regular)</Label>
                <ItemSelect value={p.billing_item_id} items={items}
                  testId={`proposal-item-${idx}`}
                  onChange={(v) => setProposals(ps => ps.map((x, i) => i === idx ? { ...x, billing_item_id: v } : x))} />
              </div>
              {p.needs_billing_item && !p.billing_item_id && (
                <Badge variant="outline" className="text-amber-600 border-amber-300">
                  No catalog match — pick one
                </Badge>
              )}
              <Button size="icon" variant="ghost"
                onClick={() => setProposals(ps => ps.filter((_, i) => i !== idx))}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setProposals(null)}>Cancel</Button>
            <Button size="sm" onClick={applyProposals} disabled={busy} data-testid="posts-proposals-apply">
              Create these posts
            </Button>
          </div>
        </CardContent></Card>
      )}

      {draft && (
        <Card><CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="post-draft">
          <div>
            <Label>Post name</Label>
            <Input value={draft.post_name} onChange={(e) => setDraft(d => ({ ...d, post_name: e.target.value }))}
              placeholder="Main Lobby, Gatehouse, Rover…" data-testid="post-name" />
          </div>
          <div>
            <Label>Post description</Label>
            <Select value={draft.post_description || '__none__'}
              onValueChange={(v) => setDraft(d => ({ ...d, post_description: v === '__none__' ? '' : v }))}>
              <SelectTrigger data-testid="post-description"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— None —</SelectItem>
                {descriptions.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Billing item — regular hours</Label>
            <ItemSelect value={draft.billing_item_id} items={items} testId="post-billing-item"
              onChange={(v) => setDraft(d => ({ ...d, billing_item_id: v }))} />
          </div>
          <div>
            <Label>Billing item — overtime</Label>
            <ItemSelect value={draft.ot_billing_item_id} items={items} testId="post-ot-item"
              onChange={(v) => setDraft(d => ({ ...d, ot_billing_item_id: v }))} />
          </div>
          <div>
            <Label>Billing item — holiday</Label>
            <ItemSelect value={draft.holiday_billing_item_id} items={items} testId="post-holiday-item"
              onChange={(v) => setDraft(d => ({ ...d, holiday_billing_item_id: v }))} />
          </div>
          <div>
            <Label>Hours type</Label>
            <Select value={draft.hours_type_id || '__none__'}
              onValueChange={(v) => setDraft(d => ({ ...d, hours_type_id: v === '__none__' ? '' : v }))}>
              <SelectTrigger data-testid="post-hours-type"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="__none__">— None —</SelectItem>
                {hoursTypes.map(h => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label>Required qualifications</Label>
            <Input value={(draft.required_qualifications || []).join(', ')}
              onChange={(e) => setDraft(d => ({
                ...d, required_qualifications: e.target.value.split(',').map(s => s.trim()).filter(Boolean),
              }))}
              placeholder="Licence types that must be current, comma separated"
              data-testid="post-quals" />
          </div>
          <div className="md:col-span-2 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => { setDraft(null); setEditingId(null); }}>Cancel</Button>
            <Button onClick={save} disabled={busy} data-testid="post-save">Save post</Button>
          </div>
        </CardContent></Card>
      )}

      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Post</TableHead>
            <TableHead>Regular</TableHead>
            <TableHead>Overtime</TableHead>
            <TableHead>Holiday</TableHead>
            <TableHead>Qualifications</TableHead>
            {canEdit && <TableHead className="w-24 text-right">Actions</TableHead>}
          </TableRow></TableHeader>
          <TableBody>
            {!posts.length ? (
              <TableRow><TableCell colSpan={6} className="p-6 text-sm text-fg-muted" data-testid="posts-empty">
                No posts yet. A job needs at least one before it can be scheduled.
              </TableCell></TableRow>
            ) : posts.map(p => (
              <TableRow key={p.id} data-testid={`post-row-${p.id}`}>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    {p.post_name}
                    {!p.is_active && <Badge variant="secondary">Inactive</Badge>}
                  </div>
                  {p.post_description && (
                    <p className="text-xs text-fg-muted">{p.post_description}</p>
                  )}
                </TableCell>
                <TableCell className="text-sm">{p.billing_item_name || itemName(p.billing_item_id)}</TableCell>
                <TableCell className="text-sm text-fg-muted">{p.ot_billing_item_name || '—'}</TableCell>
                <TableCell className="text-sm text-fg-muted">{p.holiday_billing_item_name || '—'}</TableCell>
                <TableCell className="text-xs">
                  {(p.required_qualifications || []).length
                    ? p.required_qualifications.join(', ') : '—'}
                </TableCell>
                {canEdit && (
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" data-testid={`post-edit-${p.id}`}
                      onClick={() => { setEditingId(p.id); setDraft({ ...emptyPost, ...p }); }}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => toggle(p)}
                      data-testid={`post-toggle-${p.id}`}>
                      {p.is_active ? <Archive className="w-4 h-4" /> : <ArchiveRestore className="w-4 h-4" />}
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}
